<template>
  <div class="gs-range">
    <label class="gs-range__preset">
      <span class="gs-range__preset-label">Range</span>
      <select
        class="gs-range__select"
        data-action="preset"
        :value="preset"
        aria-label="Date range preset"
        @change="onPreset($event.target.value)"
      >
        <option v-for="option in presets" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <gs-icon name="chevron-down" size="14" class="gs-range__chevron" />
    </label>

    <date-range-picker
      ref="picker"
      v-model="localCustom"
      class="gs-range__picker"
      opens="left"
      :auto-apply="true"
      :max-date="maxDate"
      @update="onCustom"
    >
      <div slot="input" class="gs-range__value">
        <gs-icon name="clock" size="14" class="gs-range__value-icon" />
        <span>{{ label }}</span>
      </div>
    </date-range-picker>
  </div>
</template>

<script>
import { RANGE_PRESETS, buildRange } from '@/utils/analyticsRange';

export default {
  name: 'DateRangePill',
  props: {
    preset: { type: String, default: 'last30' },
    custom: { type: Object, default: null },
  },
  data() {
    const range = buildRange(this.preset, this.custom);
    return {
      presets: RANGE_PRESETS,
      maxDate: new Date(),
      localCustom: {
        startDate: (this.custom && this.custom.startDate) || range.from.toDate(),
        endDate: (this.custom && this.custom.endDate) || range.to.toDate(),
      },
    };
  },
  computed: {
    label() {
      return buildRange(this.preset, this.custom).label;
    },
  },
  watch: {
    preset() {
      this.syncLocal();
    },
    custom() {
      this.syncLocal();
    },
  },
  methods: {
    syncLocal() {
      const range = buildRange(this.preset, this.custom);
      this.localCustom = { startDate: range.from.toDate(), endDate: range.to.toDate() };
    },
    onPreset(value) {
      if (value === 'custom') {
        // "Custom range" only means something once dates are picked, so open
        // the calendar instead of emitting an empty custom range.
        this.openPicker();
        return;
      }
      this.$emit('change', { preset: value, custom: null });
    },
    openPicker() {
      const picker = this.$refs.picker;
      if (picker && typeof picker.togglePicker === 'function') picker.togglePicker(true);
    },
    onCustom(value) {
      if (!value || !value.startDate || !value.endDate) return;
      this.$emit('change', { preset: 'custom', custom: { startDate: value.startDate, endDate: value.endDate } });
    },
  },
};
</script>

<style scoped>
.gs-range {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.gs-range__preset {
  position: relative;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 32px 0 14px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
}

.gs-range__preset:focus-within {
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-range__preset-label {
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-range__select {
  color: var(--gs-foreground);
  background: transparent;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.gs-range__select:focus {
  outline: none;
}

.gs-range__chevron {
  position: absolute;
  right: 12px;
  color: var(--gs-muted-foreground);
  pointer-events: none;
}

.gs-range__value {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 14px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.gs-range__value-icon {
  color: var(--gs-muted-foreground);
}

/* The picker ships its own chrome; only the space form of the deep combinator
   compiles in this repo's scoped-CSS pipeline. */
.gs-range__picker ::v-deep .reportrange-text {
  padding: 0 !important;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
}

.gs-range__picker ::v-deep .reportrange-text:hover {
  border-color: var(--gs-border-strong);
}

.gs-range__picker ::v-deep .daterangepicker {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-md);
  font-family: inherit;
}

.gs-range__picker ::v-deep .daterangepicker td.available:hover,
.gs-range__picker ::v-deep .daterangepicker th.available:hover {
  background: var(--gs-muted);
}

.gs-range__picker ::v-deep .daterangepicker td.in-range {
  color: var(--gs-foreground);
  background: var(--gs-primary-soft);
}

.gs-range__picker ::v-deep .daterangepicker td.active,
.gs-range__picker ::v-deep .daterangepicker td.active:hover {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
}

.gs-range__picker ::v-deep .daterangepicker .ranges li.active {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
}
</style>
